import cocosLogo from "@/assets/charities/cocos.webp";
import copticOrphansLogo from "@/assets/charities/coptic-orphans.webp";

export type Charity = {
  slug: "cocos" | "coptic-orphans";
  path: "/giving/cocos" | "/giving/coptic-orphans";
  name: string;
  fullName: string;
  logo: string;
  focus: string;
  blurb: string;
  site: string;
  donate: string;
  details: { label: string; value: string }[];
  metaTitle: string;
  metaDescription: string;
  story: string[];
  work: { title: string; body: string }[];
  /** The charity's own page that the program statements on this page come from. */
  sourceUrl: string;
  ways: string[];
  about: string;
};

export const charities: Charity[] = [
  {
    slug: "cocos",
    path: "/giving/cocos",
    name: "COCOS",
    fullName: "COCOS: Charity Feeding the Homeless",
    logo: cocosLogo,
    focus: "Feeding and supporting people experiencing homelessness, Sydney",
    blurb:
      "COCOS is a volunteer-based organisation in Sydney that runs street meal services and distributes clothing, blankets and hygiene items to people experiencing homelessness.",
    site: "https://cocos.org.au/",
    donate: "https://cocos.org.au/make-donation",
    metaTitle: "COCOS: Feeding Sydney's Homeless | Ajility",
    metaDescription:
      "COCOS is a volunteer-run charity feeding people experiencing homelessness in Sydney. Read about their work and donate to them directly.",
    details: [
      { label: "Cause", value: "Homelessness & food relief" },
      { label: "Where", value: "Sydney, Australia" },
      { label: "How to give", value: "One-off or recurring donation via their secure portal" },
      { label: "Model", value: "Volunteer-based" },
      { label: "ACNC registration", value: "Registered charity, 3 December 2012" },
      { label: "ABN", value: "35 131 816 531" },
      { label: "Tax deductibility", value: "DGR endorsed, 1 July 2002" },
    ],
    sourceUrl: "https://cocos.org.au/",
    story: [
      "COCOS began with a small group of volunteers running regular meal services for people sleeping rough in Sydney.",
      "COCOS states that it delivers hot meals, clothing, hygiene packs and referral support on a set weekly schedule across Sydney locations.",
      "COCOS states that its services are delivered largely by volunteers. Statements about how funds are applied are the charity's own and should be checked on the COCOS website.",
    ],
    work: [
      {
        title: "Street meal services",
        body: "Regular hot meal runs to known locations across Sydney, on a schedule people can count on.",
      },
      {
        title: "Essentials and care packs",
        body: "Clothing, blankets, hygiene items and seasonal essentials distributed alongside meals.",
      },
      {
        title: "Time with recipients",
        body: "COCOS states that volunteers stay and talk with the people they serve, not only hand over food.",
      },
      {
        title: "Referral support",
        body: "Pointing people toward the services, shelters and support they may not know exist.",
      },
    ],
    ways: [
      "Make a one-off or recurring donation through the COCOS donation page.",
      "Volunteer on a service run. Hands are as valuable as dollars.",
      "Donate goods: warm clothing, blankets and unopened hygiene items.",
    ],
    about:
      "COCOS is a volunteer-based charity in Sydney. According to COCOS, it delivers hot meals, clothing, hygiene packs and referral support to people sleeping rough on a set weekly schedule.",
  },
  {
    slug: "coptic-orphans",
    path: "/giving/coptic-orphans",
    name: "Coptic Orphans",
    fullName: "Coptic Orphans",
    logo: copticOrphansLogo,
    focus: "Education and mentoring for fatherless children, Egypt",
    blurb:
      "Coptic Orphans is an organisation that funds education for fatherless children in Egypt and pairs each child with a trained volunteer mentor.",
    site: "https://copticorphans.org/",
    donate: "https://copticorphans.org/programs/donate-now/",
    metaTitle: "Coptic Orphans: Education and Mentoring | Ajility",
    metaDescription:
      "Coptic Orphans funds education and mentoring for fatherless children in Egypt. Read about their work, sponsor a child or donate directly.",
    details: [
      { label: "Cause", value: "Child education & mentoring" },
      { label: "Where", value: "Egypt, with supporters worldwide" },
      { label: "How to give", value: "Sponsor a child or make a one-off donation" },
      { label: "Model", value: "Trained volunteer mentors" },
      { label: "ACNC registration", value: "Registered charity, 3 December 2012" },
      { label: "ABN", value: "27 138 388 851" },
      { label: "Tax deductibility", value: "DGR endorsed, 27 January 2016" },
    ],
    sourceUrl: "https://copticorphans.org/",
    story: [
      "Coptic Orphans works with fatherless children in Egypt, focusing on education rather than short-term relief.",
      "Coptic Orphans states that trained volunteer mentors visit the same children over a period of years and track school progress, health and family circumstances.",
      "Coptic Orphans states that children in its programs stay in school and go on to further study or work. Statements about program outcomes are the charity's own and are published on the Coptic Orphans website.",
    ],
    work: [
      {
        title: "Not Alone, child sponsorship",
        body: "Ongoing support for a child's education and wellbeing, delivered through a trained local mentor who visits regularly.",
      },
      {
        title: "Serve to Learn",
        body: "Coptic Orphans states that volunteers travel to teach English and life skills to children in its programs.",
      },
      {
        title: "B'edaya, girls' empowerment",
        body: "Coptic Orphans states that this program teaches skills, confidence and financial literacy to young women.",
      },
      {
        title: "Valuable Girl Project",
        body: "Coptic Orphans states that older girls mentor younger girls through this program.",
      },
    ],
    ways: [
      "Sponsor a child through the Not Alone program.",
      "Make a one-off or recurring donation online.",
      "Volunteer with Serve to Learn if you can travel.",
      "Fundraise for Coptic Orphans through their own fundraising pages.",
    ],
    about:
      "Coptic Orphans funds education for fatherless children in Egypt and pairs each child with a trained volunteer mentor. According to Coptic Orphans, mentors visit over a period of years and track school progress, health and family circumstances.",
  },
];

export function getCharity(slug: string) {
  return charities.find((c) => c.slug === slug);
}
